import { useEffect, useMemo, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { ArrowLeft, BadgeCheck, Briefcase, Edit, IdCard, Loader2, Mail, Phone, Plus, Search, ShieldCheck, Trash2, Truck, UserRound, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';

type CorporateDriver = {
  id: string;
  driverId?: string;
  fullName: string;
  phone: string;
  email: string;
  tcNumber: string;
  licenseNumber: string;
  licenseClass: string;
  city: string;
  district: string;
  address: string;
  tractorPlate: string;
  trailerPlate: string;
  vehicleType: string;
  status: string;
  documentCount: number;
  createdAt?: string | null;
};

type DriverForm = {
  full_name: string;
  phone: string;
  email: string;
  tc_number: string;
  license_number: string;
  license_class: string;
  city: string;
  district: string;
  address: string;
  cekici_plaka: string;
  dorse_plaka: string;
  vehicle_type: string;
  invoice_title: string;
  tax_office: string;
  tax_number: string;
};

const emptyForm: DriverForm = {
  full_name: '',
  phone: '',
  email: '',
  tc_number: '',
  license_number: '',
  license_class: 'E',
  city: '',
  district: '',
  address: '',
  cekici_plaka: '',
  dorse_plaka: '',
  vehicle_type: '',
  invoice_title: '',
  tax_office: '',
  tax_number: '',
};

const normalizePlate = (value: string) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const normalizeText = (value: string) => String(value || '').trim().toLocaleLowerCase('tr-TR');

const mapDriver = (item: any): CorporateDriver => ({
  id: String(item.id || item.application_id || ''),
  driverId: item.driver_id ? String(item.driver_id) : undefined,
  fullName: item.full_name || item.name || item.driver_name || '',
  phone: item.phone || '',
  email: item.email || '',
  tcNumber: item.tc_number || '',
  licenseNumber: item.license_number || '',
  licenseClass: item.license_class || 'E',
  city: item.city || '',
  district: item.district || '',
  address: item.address || '',
  tractorPlate: item.cekici_plaka || item.vehicle_plate || '',
  trailerPlate: item.dorse_plaka || item.trailer_plate || '',
  vehicleType: item.vehicle_type || '',
  status: item.status || 'approved',
  documentCount: Number(item.document_count || item.docCount || 0),
  createdAt: item.created_at || null,
});

const buildForm = (driver: CorporateDriver): DriverForm => ({
  ...emptyForm,
  full_name: driver.fullName,
  phone: driver.phone,
  email: driver.email,
  tc_number: driver.tcNumber,
  license_number: driver.licenseNumber,
  license_class: driver.licenseClass || 'E',
  city: driver.city,
  district: driver.district,
  address: driver.address,
  cekici_plaka: driver.tractorPlate,
  dorse_plaka: driver.trailerPlate,
  vehicle_type: driver.vehicleType,
});

export function CorporateDrivers() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<CorporateDriver[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDriver, setEditingDriver] = useState<CorporateDriver | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<DriverForm>(emptyForm);

  const loadData = async () => {
    try {
      setLoading(true);
      const [driversRes, vehiclesRes] = await Promise.all([
        driverService.getApplications({ status: 'approved', all: true }),
        vehicleService.getVehicles({ ownerType: 'external' }).catch(() => ({ data: [] })),
      ]);
      const rawDrivers = Array.isArray((driversRes as any)?.data) ? (driversRes as any).data : [];
      const rawVehicles = Array.isArray((vehiclesRes as any)?.data) ? (vehiclesRes as any).data : [];
      setDrivers(rawDrivers.map(mapDriver));
      setVehicles(rawVehicles);
    } catch (error: any) {
      console.error('Corporate drivers load failed:', error);
      toast.error(error?.message || 'Sürücüler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const filteredDrivers = useMemo(() => {
    const query = normalizeText(search);
    if (!query) return drivers;
    return drivers.filter((driver) => [
      driver.fullName,
      driver.phone,
      driver.email,
      driver.tractorPlate,
      driver.trailerPlate,
      driver.city,
      driver.licenseNumber,
    ].some((value) => normalizeText(String(value || '')).includes(query)));
  }, [drivers, search]);

  const stats = useMemo(() => ({
    total: drivers.length,
    withVehicle: drivers.filter((driver) => driver.tractorPlate).length,
    missingDocs: drivers.filter((driver) => Number(driver.documentCount || 0) === 0).length,
    active: drivers.filter((driver) => ['approved', 'active'].includes(String(driver.status || '').toLowerCase())).length,
  }), [drivers]);

  const openCreate = () => {
    setEditingDriver(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (driver: CorporateDriver) => {
    setEditingDriver(driver);
    setForm(buildForm(driver));
    setFormOpen(true);
  };

  const selectVehicle = (vehicleId: string) => {
    const vehicle = vehicles.find((item) => String(item.id) === vehicleId);
    if (!vehicle) return;
    setForm((current) => ({
      ...current,
      cekici_plaka: vehicle.cekici_plaka || vehicle.plate || '',
      dorse_plaka: vehicle.dorse_plaka || vehicle.trailerPlate || '',
      vehicle_type: vehicle.vehicle_type || vehicle.vehicleType || current.vehicle_type,
    }));
  };

  const saveDriver = async () => {
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error('Ad soyad ve telefon zorunludur');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        cekici_plaka: normalizePlate(form.cekici_plaka),
        dorse_plaka: normalizePlate(form.dorse_plaka),
      };
      if (editingDriver) {
        await driverService.updateApplication(editingDriver.id, payload);
        toast.success('Sürücü güncellendi');
      } else {
        await driverService.createApplication(payload);
        toast.success('Sürücü eklendi. Mobil giriş şifresi varsayılan olarak 123456 oluşturuldu.');
      }
      setFormOpen(false);
      await loadData();
    } catch (error: any) {
      console.error('Corporate driver save failed:', error);
      toast.error(error?.message || 'Sürücü kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const removeDriver = async (driver: CorporateDriver) => {
    if (!window.confirm(`${driver.fullName || 'Sürücü'} kaydını silmek istiyor musunuz?`)) return;
    try {
      await driverService.deleteApplication(driver.id);
      toast.success('Sürücü silindi');
      await loadData();
    } catch (error: any) {
      console.error('Corporate driver delete failed:', error);
      toast.error(error?.message || 'Sürücü silinemedi');
    }
  };

  return (
    <div className="min-h-[calc(100vh-7rem)] bg-slate-50 pb-28 text-slate-900 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-5 p-3 sm:p-0">
        <section className="overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl sm:p-7">
          <button type="button" onClick={() => navigate('/')} className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-white/15">
            <ArrowLeft className="h-4 w-4" /> Panelime dön
          </button>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-300">Kurumsal Sürücü Yönetimi</div>
              <h1 className="mt-3 text-3xl font-black sm:text-4xl">Sürücülerim</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Firmanıza bağlı mobil spot sürücüleri, çekici eşleşmelerini ve iletişim bilgilerini admin panelinden bağımsız yönetin.</p>
            </div>
            <button type="button" onClick={openCreate} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-950/30 hover:bg-orange-600">
              <Plus className="h-5 w-5" /> Sürücü Ekle
            </button>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-xs font-semibold text-slate-500">Toplam Sürücü</div><div className="mt-2 text-3xl font-black">{stats.total}</div></div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm"><div className="text-xs font-semibold text-emerald-700">Aktif</div><div className="mt-2 text-3xl font-black text-emerald-900">{stats.active}</div></div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 shadow-sm"><div className="text-xs font-semibold text-blue-700">Çekicili</div><div className="mt-2 text-3xl font-black text-blue-900">{stats.withVehicle}</div></div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm"><div className="text-xs font-semibold text-amber-700">Evrak Eksik</div><div className="mt-2 text-3xl font-black text-amber-900">{stats.missingDocs}</div></div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Sürücü, telefon, plaka veya şehir ara" className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-12 pr-4 text-sm outline-none focus:border-orange-400 focus:bg-white" />
          </div>
        </section>

        {loading ? (
          <div className="flex items-center justify-center rounded-3xl border border-slate-200 bg-white py-16 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sürücüler yükleniyor...</div>
        ) : filteredDrivers.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center">
            <UserRound className="mx-auto h-12 w-12 text-slate-300" />
            <div className="mt-3 text-lg font-bold">Sürücü bulunamadı</div>
            <p className="mt-1 text-sm text-slate-500">Firmanız için ilk sürücüyü ekleyerek başlayabilirsiniz.</p>
            <button type="button" onClick={openCreate} className="mt-5 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-bold text-white hover:bg-orange-600">Sürücü Ekle</button>
          </div>
        ) : (
          <section className="grid gap-4 lg:grid-cols-2">
            {filteredDrivers.map((driver) => (
              <article key={driver.id} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white"><UserRound className="h-6 w-6" /></div>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-black text-slate-900">{driver.fullName || '-'}</h2>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {driver.phone || '-'}</span>
                        {driver.email ? <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {driver.email}</span> : null}
                      </div>
                    </div>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">Aktif</span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl bg-slate-50 p-3"><div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Truck className="h-4 w-4" /> Çekici</div><div className="mt-1 font-bold">{driver.tractorPlate || '-'}</div></div>
                  <div className="rounded-2xl bg-slate-50 p-3"><div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Briefcase className="h-4 w-4" /> Dorse</div><div className="mt-1 font-bold">{driver.trailerPlate || '-'}</div></div>
                  <div className="rounded-2xl bg-slate-50 p-3"><div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><IdCard className="h-4 w-4" /> Ehliyet</div><div className="mt-1 font-bold">{driver.licenseNumber || '-'} {driver.licenseClass ? `/${driver.licenseClass}` : ''}</div></div>
                  <div className="rounded-2xl bg-slate-50 p-3"><div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><ShieldCheck className="h-4 w-4" /> Evrak</div><div className="mt-1 font-bold">{driver.documentCount || 0} kayıt</div></div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500"><BadgeCheck className="h-4 w-4 text-emerald-600" /> Mobil sürücü hesabı aktif</div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => openEdit(driver)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"><Edit className="h-4 w-4" /> Düzenle</button>
                    <button type="button" onClick={() => void removeDriver(driver)} className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 hover:bg-rose-100"><Trash2 className="h-4 w-4" /> Sil</button>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}
      </div>

      {formOpen ? (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div><div className="text-lg font-black">{editingDriver ? 'Sürücü Düzenle' : 'Sürücü Ekle'}</div><div className="text-sm text-slate-500">Kurumsal mobil sürücü kaydı</div></div>
              <button type="button" onClick={() => setFormOpen(false)} disabled={saving} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[calc(92vh-145px)] overflow-auto px-5 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Ad Soyad" value={form.full_name} onChange={(value) => setForm((current) => ({ ...current, full_name: value }))} required />
                <Field label="Telefon" value={form.phone} onChange={(value) => setForm((current) => ({ ...current, phone: value }))} required inputMode="tel" />
                <Field label="E-posta" value={form.email} onChange={(value) => setForm((current) => ({ ...current, email: value }))} inputMode="email" />
                <Field label="TC Kimlik No" value={form.tc_number} onChange={(value) => setForm((current) => ({ ...current, tc_number: value }))} />
                <Field label="Ehliyet No" value={form.license_number} onChange={(value) => setForm((current) => ({ ...current, license_number: value }))} />
                <Field label="Ehliyet Sınıfı" value={form.license_class} onChange={(value) => setForm((current) => ({ ...current, license_class: value }))} />
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Firmanıza Bağlı Çekici Seç</label>
                  <select onChange={(event) => selectVehicle(event.target.value)} value="" className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400">
                    <option value="">Çekici seçerek plaka/dorse doldur</option>
                    {vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.cekici_plaka || '-'} {vehicle.dorse_plaka ? `/ ${vehicle.dorse_plaka}` : ''}</option>)}
                  </select>
                </div>
                <Field label="Çekici Plaka" value={form.cekici_plaka} onChange={(value) => setForm((current) => ({ ...current, cekici_plaka: value }))} />
                <Field label="Dorse Plaka" value={form.dorse_plaka} onChange={(value) => setForm((current) => ({ ...current, dorse_plaka: value }))} />
                <Field label="Araç Tipi" value={form.vehicle_type} onChange={(value) => setForm((current) => ({ ...current, vehicle_type: value }))} />
                <Field label="Şehir" value={form.city} onChange={(value) => setForm((current) => ({ ...current, city: value }))} />
                <Field label="İlçe" value={form.district} onChange={(value) => setForm((current) => ({ ...current, district: value }))} />
                <Field label="Fatura Ünvanı" value={form.invoice_title} onChange={(value) => setForm((current) => ({ ...current, invoice_title: value }))} />
                <Field label="Vergi Dairesi" value={form.tax_office} onChange={(value) => setForm((current) => ({ ...current, tax_office: value }))} />
                <Field label="Vergi No" value={form.tax_number} onChange={(value) => setForm((current) => ({ ...current, tax_number: value }))} />
                <div className="sm:col-span-2"><Field label="Adres" value={form.address} onChange={(value) => setForm((current) => ({ ...current, address: value }))} /></div>
              </div>
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button type="button" onClick={() => setFormOpen(false)} disabled={saving} className="flex-1 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 disabled:opacity-60">Vazgeç</button>
              <button type="button" onClick={() => void saveDriver()} disabled={saving} className="flex-1 rounded-2xl bg-orange-500 px-4 py-3 text-sm font-bold text-white disabled:opacity-60">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, onChange, required, inputMode }: { label: string; value: string; onChange: (value: string) => void; required?: boolean; inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'] }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}{required ? ' *' : ''}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} inputMode={inputMode} className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400" />
    </label>
  );
}

export default CorporateDrivers;
